import React, { useState, useEffect, useCallback } from 'react';
import { isPickupType } from '../../../constants/bookingTypes';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import EditBooking from '../Bookings/EditBooking';
import './warehouse.css';
import { colorOf } from '../../../constants/areas';

/**
 * ✅ Build LH pickup entries from backend summary.
 * Returns ordered array like: [ ['BS', 45], ['DC', 52], ... ]
 */
function buildLHPickupsEntries(lhPickupSummary, itemShortNames) {
    if (!lhPickupSummary?.pickups) return [];

    const shortOrder = ['BS', 'SMALL BS', 'DC', 'SMALL DC', 'PC', 'BT', 'HT', 'FT', 'BM', 'BEACH'];

    const mapped = lhPickupSummary.pickups.map(p => {
        const short = itemShortNames[p.name] || p.name;
        return { label: short, count: Number(p.total_pickup || 0) };
    });

    const mapByLabel = {};
    mapped.forEach(x => {
        mapByLabel[x.label] = (mapByLabel[x.label] || 0) + x.count;
    });

    return shortOrder
        .filter(label => mapByLabel[label] > 0)
        .map(label => [label, mapByLabel[label]]);
}

/**
 * ✅ Build LH drop-off entries from backend summary.
 * Returns ordered array like: [ ['BS', 45], ['DC', 52], ... ]
 */
function buildLHDropoffsEntries(lhDropoffSummary, itemShortNames) {
    if (!lhDropoffSummary?.dropoffs) return [];

    const shortOrder = ['BS', 'SMALL BS', 'DC', 'SMALL DC', 'PC', 'BT', 'HT', 'FT', 'BM', 'BEACH'];

    const mapped = lhDropoffSummary.dropoffs.map(d => {
        const short = itemShortNames[d.name] || d.name;
        return { label: short, count: Number(d.total_dropoff || 0) };
    });

    const mapByLabel = {};
    mapped.forEach(x => {
        mapByLabel[x.label] = (mapByLabel[x.label] || 0) + x.count;
    });

    return shortOrder
        .filter(label => mapByLabel[label] > 0)
        .map(label => [label, mapByLabel[label]]);
}

function Warehouse() {
    const [selectedDate, setSelectedDate] = useState('');
    const [report, setReport] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    const [lhPickupSummary, setLhPickupSummary] = useState(null);
    const [lhPickupLoading, setLhPickupLoading] = useState(false);

    const [editingBookingId, setEditingBookingId] = useState(null);
    const [copied, setCopied] = useState(false);

    const [lhDropoffSummary, setLhDropoffSummary] = useState(null);
    const [lhDropoffLoading, setLhDropoffLoading] = useState(false);

    // ✅ Role check
    const userRole = localStorage.getItem('userRole');
    const isAdminOrManager = userRole === 'admin' || userRole === 'manager' || userRole === 'supervisor';

    const fetchReport = useCallback(
        async (date) => {
            if (!date) return;

            setLoading(true);
            setLhPickupLoading(true);
            setLhDropoffLoading(true);
            setError('');
            setCopied(false);

            try {
                const [reportRes, lhPickupRes, lhDropoffRes] = await Promise.all([
                    axios.get(`/api/laundry/warehouse-duty/${date}`),
                    axios.get(`/api/laundry/lh-pickup-summary/${date}`),
                    axios.get(`/api/laundry/lh-dropoff-summary/${date}`)
                ]);

                setReport(reportRes.data.bookings);
                setLhPickupSummary(lhPickupRes.data);
                setLhDropoffSummary(lhDropoffRes.data);

                navigate(`?date=${date}`, { replace: true });
            } catch (error) {
                console.error('Error fetching warehouse report:', error);
                setError('Failed to load report. Please try again.');
                setLhPickupSummary(null);
                setLhDropoffSummary(null);
            } finally {
                setLoading(false);
                setLhPickupLoading(false);
                setLhDropoffLoading(false);
            }
        },
        [navigate]
    );

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const date = params.get('date');
        if (date) {
            setSelectedDate(date);
            fetchReport(date);
        }
    }, [location.search, fetchReport]);

    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
    };


    // 🔹 Map add-on item names to linen pieces
    const ADDON_LINEN_MAP = {
        'Extra Double Linen Hire (Old)': {
            BS: 1, DC: 1, PC: 4, BT: 2, HT: 2, FT: 2, BM: 1,
        },
        'Extra Single Linen Hire (Old)': {
            SMALL_BS: 1, SMALL_DC: 1, PC: 2, BT: 1, HT: 1, FT: 1,
        },

        'Extra Double Linen Hire Set': {
            BS: 1, DC: 1, PC: 4, BT: 2, HT: 2, FT: 2, BM: 1,
        },
        'Extra Single Linen Hire Set': {
            SMALL_BS: 1, SMALL_DC: 1, PC: 2, BT: 1, HT: 1, FT: 1,
        },

        'Extra LH Bedsheet': { BS: 1 },
        'Extra LH Small Bedsheet': { SMALL_BS: 1 },
        'Extra LH Super King Bedsheet': { SK_BS: 1 },
        'Extra LH Duvet Cover': { DC: 1 },
        'Extra LH Small Duvet Cover': { SMALL_DC: 1 },
        'Extra LH Super King Duvet Cover': { SK_DC: 1 },
        'Extra LH Pillowcase': { PC: 1 },
        'Extra LH Bath Towel': { BT: 1 },
        'Extra LH Hand Towel': { HT: 1 },
        'Extra LH Face Towel': { FT: 1 },
        'Extra LH Bathmat': { BM: 1 },
        'Extra LH Beach Towel': { BEACH: 1 },

        // ✅ Linen Hire type add-ons (these are add-ons, not replacements)
        // Bed Linens Only = 1 BS, 1 DC, 4 PC
        // Towels Only = 2 BT, 2 HT, 2 FT, 1 BM
        'Linen Hire (Bed Linens Only) - (Old)': { BS: 1, DC: 1, PC: 4 },
        'Linen Hire (Towels Only) - (Old)': { BT: 2, HT: 2, FT: 2, BM: 1 },
        'Linen Hire - Bed Linens Only': { BS: 1, DC: 1, PC: 4 },
        'Linen Hire - Towels Only': { BT: 2, HT: 2, FT: 2, BM: 1 },
    };

    const ADDON_DISPLAY_LABELS = {
        BS: 'BS',
        SMALL_BS: 'Small BS',
        SK_BS: 'Super King BS',
        DC: 'DC',
        SMALL_DC: 'Small DC',
        SK_DC: 'Super King DC',
        PC: 'PC',
        BT: 'BT',
        HT: 'HT',
        FT: 'FT',
        BM: 'BM',
        BEACH: 'BEACH',
    };

    const ADDON_DISPLAY_ORDER = [
        'BS', 'SMALL_BS', 'SK_BS', 'DC', 'SMALL_DC', 'SK_DC', 'PC', 'BT', 'HT', 'FT', 'BM', 'BEACH',
    ];

    const getAddonSummary = (addons) => {
        if (!Array.isArray(addons) || addons.length === 0) {
            return { linenSummary: null, hasCarpet: false };
        }

        const totals = {};
        let hasCarpet = false;
        const linenAddons = [];

        addons.forEach((addon) => {
            const name = (addon.item_name || '').trim();
            const qty = Number(addon.quantity) || 0;
            if (qty <= 0) return;

            if (name === 'Carpet / Upholstery deep clean') {
                hasCarpet = true;
                return;
            }

            linenAddons.push(addon);
        });

        linenAddons.forEach((addon) => {
            const pattern = ADDON_LINEN_MAP[addon.item_name];
            if (!pattern) return;

            const qty = Number(addon.quantity) || 0;
            if (qty <= 0) return;

            Object.entries(pattern).forEach(([code, baseQty]) => {
                totals[code] = (totals[code] || 0) + baseQty * qty;
            });
        });

        const parts = ADDON_DISPLAY_ORDER
            .filter((code) => totals[code] && totals[code] > 0)
            .map((code) => `${totals[code]} ${ADDON_DISPLAY_LABELS[code]}`);

        const linenSummary = parts.length > 0
            ? `Add-on: ${parts.join(', ')}`
            : null;

        return { linenSummary, hasCarpet };
    };

    // Super King packs like a double (PC/towels ×2 per bed) but its bedsheet
    // and duvet cover are their own size (SK BS / SK DC).
    const calculateLinen = (singleBeds, doubleBeds, bathrooms, includeBeachTowel = false, superKingBeds = 0) => {
        const roundedBathrooms = Math.ceil(bathrooms);
        const sk = Number(superKingBeds) || 0;
        const hasBeds = singleBeds > 0 || doubleBeds > 0 || sk > 0;
        const btQty = hasBeds ? (singleBeds + (doubleBeds + sk) * 2) : 0;

        return {
            BS: doubleBeds > 0 ? `${doubleBeds} BS` : null,
            smallBS: singleBeds > 0 ? `${singleBeds} SMALL BS` : null,
            skBS: sk > 0 ? `${sk} SUPER KING BS` : null,
            DC: doubleBeds > 0 ? `${doubleBeds} DC` : null,
            smallDC: singleBeds > 0 ? `${singleBeds} SMALL DC` : null,
            skDC: sk > 0 ? `${sk} SUPER KING DC` : null,
            PC: hasBeds ? `${singleBeds * 2 + (doubleBeds + sk) * 4} PC` : null,
            BT: hasBeds ? `${btQty} BT` : null,
            HT: hasBeds ? `${singleBeds + (doubleBeds + sk) * 2} HT` : null,
            FT: hasBeds ? `${singleBeds + (doubleBeds + sk) * 2} FT` : null,
            BM: roundedBathrooms > 0 ? `${roundedBathrooms} BM` : null,
            BEACH: includeBeachTowel && btQty > 0 ? `${btQty} BEACH` : null,
        };
    };

    const itemShortNames = {
        'Small Bedsheet': "SMALL BS",
        'Small Duvet Cover': "SMALL DC",
        'Super King Bedsheet': "SUPER KING BS",
        'Super King Duvet Cover': "SUPER KING DC",
        'Bedsheet': 'BS',
        'Duvet Cover': 'DC',
        'Pillowcase': 'PC',
        'Bath Towel': 'BT',
        'Hand Towel': 'HT',
        'Face Towel': 'FT',
        'Bathmat': 'BM',
        'Beach Towel': 'BEACH'
    };

    const orderedItemShortNames = [
        'Bedsheet',
        'Small Bedsheet',
        'Super King Bedsheet',
        'Duvet Cover',
        'Small Duvet Cover',
        'Super King Duvet Cover',
        'Pillowcase',
        'Bath Towel',
        'Hand Towel',
        'Face Towel',
        'Bathmat',
        'Beach Towel'
    ];

    const getLinenHireSummary = () => {
        const summary = {};

        const codeToLabel = {
            BS: 'BS',
            SMALL_BS: 'SMALL BS',
            SK_BS: 'SUPER KING BS',
            DC: 'DC',
            SMALL_DC: 'SMALL DC',
            SK_DC: 'SUPER KING DC',
            PC: 'PC',
            BT: 'BT',
            HT: 'HT',
            FT: 'FT',
            BM: 'BM',
            BEACH: 'BEACH',
        };

        report
            .filter(booking => booking.is_linen_hire && booking.is_laundry)
            .forEach(booking => {
                // Base linen
                if (booking.is_custom_sq && booking.standard_items?.length) {
                    booking.standard_items.forEach(item => {
                        const shortLabel = itemShortNames[item.item_name];
                        if (!shortLabel) return;
                        if (!summary[shortLabel]) summary[shortLabel] = 0;
                        summary[shortLabel] += Number(item.standard_quantity || 0);
                    });
                } else {
                    const linenCounts = calculateLinen(
                        booking.single_beds,
                        booking.double_beds,
                        booking.bathrooms,
                        booking.is_beach_towel,
                        booking.super_king_beds
                    );

                    Object.entries(linenCounts).forEach(([_, value]) => {
                        if (!value) return;
                        const [qty, ...labelParts] = value.split(' ');
                        const label = labelParts.join(' ');
                        if (!summary[label]) summary[label] = 0;
                        summary[label] += parseInt(qty, 10);
                    });
                }

                // Add-ons (includes Bed Linens Only + Towels Only as add-ons now)
                if (Array.isArray(booking.addons) && booking.addons.length > 0) {
                    booking.addons.forEach(addon => {
                        const name = (addon.item_name || '').trim();
                        const qty = Number(addon.quantity) || 0;
                        if (qty <= 0) return;

                        if (name === 'Carpet / Upholstery deep clean') return;

                        const pattern = ADDON_LINEN_MAP[name];
                        if (!pattern) return;

                        Object.entries(pattern).forEach(([code, baseQty]) => {
                            const label = codeToLabel[code];
                            if (!label) return;
                            if (!summary[label]) summary[label] = 0;
                            summary[label] += baseQty * qty;
                        });
                    });
                }
            });

        return summary;
    };

    // First amenities booking for the property: the warehouse packs the
    // dispensers as well. The server decides "first" and sends the counts.
    const DISPENSER_HIGHLIGHT = '#8ce99a';
    const dispenserCounts = (booking) => (booking.dispensers?.length
        ? booking.dispensers.map((d) => `${d.quantity} ${d.label}`).join(', ')
        : 'bedroom count missing on the property');

    const getDutyInfo = (booking) => {
        let dutyText = '';
        let linenText = '';
        let laundryText = '';
        let laundryStatusText = '';

        const now = new Date(booking.booking_start).getTime();
        const lastBooking = new Date(booking.last_booking_date).getTime();
        const isUrgent = (now - lastBooking) <= 60 * 60 * 1000 * 60; // 60 hours

        if (!booking.is_laundry && !booking.is_amenities) {
            return { duty: <span style={{ backgroundColor: 'red' }}>No preparation required</span> };
        }

        if (booking.is_laundry) {
            if (booking.is_linen_hire) {
                dutyText = <span style={{ backgroundColor: 'yellow' }}>Pack Linen Hire</span>;

                if (booking.is_custom_sq && booking.standard_items?.length) {
                    const sortedItems = orderedItemShortNames
                        .map(name => booking.standard_items.find(item => item.item_name === name))
                        .filter(Boolean);

                    linenText = sortedItems
                        .map(item => `${item.standard_quantity} ${itemShortNames[item.item_name]}`)
                        .join(', ');
                } else {
                    const linenCounts = calculateLinen(
                        booking.single_beds,
                        booking.double_beds,
                        booking.bathrooms,
                        booking.is_beach_towel,
                        booking.super_king_beds
                    );
                    linenText = Object.values(linenCounts).filter(Boolean).join(', ');
                }
            } else {
                dutyText = 'Pack Laundry';
            }
        }

        if (booking.client_name === "Mai De Luxe" && booking.standard_items?.length && !booking.is_linen_hire) {
            const sortedItems = orderedItemShortNames
                .map(name => booking.standard_items.find(item => item.item_name === name))
                .filter(Boolean);

            laundryText = (
                <span>
                    <span style={{ backgroundColor: 'silver' }}>Prepare From MDL:</span>{' '}
                    {sortedItems
                        .map(item => `${item.standard_quantity} ${item.item_name ? itemShortNames[item.item_name] : ''}`)
                        .join(', ')}
                </span>
            );
        } else if (!booking.is_linen_hire) {
            const laundryStatusStyle = {
                color: booking.laundry_status === 'completed' ? 'inherit' : 'red',
            };

            laundryStatusText = booking.laundry_status
                ? (
                    <Link to={`/laundry/${booking.laundry_count_id}`} className='laundry-link'>
                        Laundry Status: <span style={laundryStatusStyle}>{booking.laundry_status}</span>, Packed in {booking.laundry_bags} bags
                        {isUrgent && <span style={{ color: 'red', marginLeft: '5px' }}>URGENT</span>}
                    </Link>
                )
                : (
                    <Link to={`/laundry/${booking.id}`} style={{ color: 'red' }}>
                        Laundry Status: Not Confirmed
                    </Link>
                );
        }

        if (isPickupType(booking.booking_type)) {
            dutyText = 'Prepare empty blue bag with tag';
        } else if (booking.booking_type === 'Dropoff') {
            dutyText = 'Prepare Linens';
        }

        let amenitiesParts = [];

        if (booking.is_amenities) {
            amenitiesParts.push(<span key="amenities">Pack Amenities</span>);
        }

        if (booking.is_nespresso) {
            amenitiesParts.push(
                <span key="nesp" style={{ backgroundColor: '#ff67cfff' }}>
                    Pack Nespresso Capsules (SMALL)
                </span>
            );
        }

        if (booking.is_dolce_gusto) {
            amenitiesParts.push(
                <span key="dg" style={{ backgroundColor: '#67b1ffff' }}>
                    Pack Dolce Gusto Capsules (BIG)
                </span>
            );
        }

        const amenitiesText =
            amenitiesParts.length > 0 ? (
                <>
                    {amenitiesParts.map((node, idx) => (
                        <React.Fragment key={idx}>
                            {idx === 0 ? '' : ' + '}
                            {node}
                        </React.Fragment>
                    ))}
                </>
            ) : (
                <span style={{ backgroundColor: 'red' }}>NO AMENITIES</span>
            );

        return {
            duty: dutyText,
            amenities: amenitiesText,
            linenText,
            laundryText,
            laundryStatus: laundryStatusText,
        };
    };

    // EVERY BOOKING IN EXACTLY ONE GROUP (2026-10-18). This used to be five
    // separate filters, and two combinations fell through all of them: a
    // linen-hire property booked WITH amenities but WITHOUT laundry matched
    // none, so it vanished from the list and the copied report — Forte Tower 1
    // unit 2801 (booking 22871) was never prepared. A Mai De Luxe booking with
    // nothing to prepare matched two and showed twice. One ordered rule per
    // booking, same group order as before; within a group, the day's order.
    const groupOf = (booking) => {
        if (booking.is_linen_hire && booking.is_laundry) return 0;                // linen hire to pack
        if (booking.client_name === 'Mai De Luxe' && !booking.is_linen_hire) return 1;
        if (booking.is_amenities) return 2;                                        // amenities to pack
        if (booking.is_laundry) return 3;                                          // laundry, no amenities
        return 4;                                                                  // nothing to prepare
    };
    const sortReport = (report) => {
        const groups = [[], [], [], [], []];
        (report || []).forEach((booking) => groups[groupOf(booking)].push(booking));
        return groups.flat();
    };

    const sortedReport = sortReport(report);

    /**
     * 🔹 Build minimal HTML for clipboard (plain text layout + color highlights)
     * - NO LH pickup totals copied
     * - Colors follow the on-screen logic as closely as possible
     * - Apartment line colored per area
     */
    const buildCopyHtmlForReport = () => {
        if (!sortedReport.length) return '';

        const lines = [];

        // Header
        lines.push(
            `<div><strong>Warehouse Duty Report${selectedDate ? ' - ' + selectedDate : ''}</strong></div>`
        );

        // Blank line before Linen Hire Total
        lines.push('<div>&nbsp;</div>');

        // Linen Hire Total (expected packing + add-ons)
        const lhSummary = getLinenHireSummary();
        const lhOrder = ['BS', 'SMALL BS', 'SUPER KING BS', 'DC', 'SMALL DC', 'SUPER KING DC', 'PC', 'BT', 'HT', 'FT', 'BM', 'BEACH'];
        const lhEntries = lhOrder
            .filter(key => lhSummary[key] != null)
            .map(key => `${key}: ${lhSummary[key]} pcs`);

        if (lhEntries.length > 0) {
            lines.push(
                `<div><strong>Linen Hire Total:</strong> ${lhEntries.join(', ')}</div>`
            );
        }

        // Blank line AFTER Linen Hire Total
        lines.push('<div>&nbsp;</div>');

        // Each booking line
        sortedReport.forEach((booking, index) => {
            const formattedPropertyId = `#${String(booking.property_id).padStart(3, '0')}`;
            const areaColor = colorOf(booking.area);

            // Header line for booking, colored per area (like the link)
            lines.push(
                `<div><strong><span style="color:${areaColor};">${index + 1}. ${formattedPropertyId} - ${booking.client_name} - ${booking.building_name} - Unit ${booking.unit_number} (${booking.bedrooms === 0 ? 'S' : booking.bedrooms} - ${booking.bathrooms})</span></strong></div>`
            );

            // 🔹 Highlights line (duty + amenities + flags)
            const dutyParts = [];
            const nowTs = booking.booking_start ? new Date(booking.booking_start).getTime() : null;
            const lastTs = booking.last_booking_date ? new Date(booking.last_booking_date).getTime() : null;
            const isUrgent =
                nowTs != null &&
                lastTs != null &&
                (nowTs - lastTs) <= 60 * 60 * 1000 * 60; // 60 hours

            // Mirror getDutyInfo logic as plain text + inline colors
            if (!booking.is_laundry && !booking.is_amenities) {
                dutyParts.push('<span style="background-color:red;">No preparation required</span>');
            } else {
                // Main duty
                if (isPickupType(booking.booking_type)) {
                    dutyParts.push('Prepare empty blue bag with tag');
                } else if (booking.booking_type === 'Dropoff') {
                    dutyParts.push('Prepare Linens');
                } else if (booking.is_laundry) {
                    if (booking.is_linen_hire) {
                        dutyParts.push('<span style="background-color:yellow;">Pack Linen Hire</span>');
                    } else {
                        dutyParts.push('Pack Laundry');
                    }
                }

                // Amenities vs NO AMENITIES
                if (booking.is_amenities) {
                    dutyParts.push('Pack Amenities');
                } else {
                    dutyParts.push('<span style="background-color:red;">NO AMENITIES</span>');
                }

                // Nespresso / Dolce Gusto
                if (booking.is_nespresso) {
                    dutyParts.push('<span style="background-color:#ff67cfff;">Pack Nespresso Capsules (SMALL)</span>');
                }

                if (booking.is_dolce_gusto) {
                    dutyParts.push('<span style="background-color:#67b1ffff;">Pack Dolce Gusto Capsules (BIG)</span>');
                }
            }

            // URGENT flag (we attach it here to keep it visible)
            if (isUrgent) {
                dutyParts.push('<span style="background-color:#ffcc80;">URGENT</span>');
            }

            if (dutyParts.length > 0) {
                lines.push(`<div>${dutyParts.join(' + ')}</div>`);
            }

            if (booking.first_amenities) {
                lines.push(
                    `<div><span style="background-color:${DISPENSER_HIGHLIGHT};"><strong>PACK DISPENSERS</strong></span>: ${dispenserCounts(booking)}</div>`
                );
            }

            // 🔹 Linen details for LH bookings
            if (booking.is_laundry && booking.is_linen_hire) {
                let linenText = '';

                if (booking.is_custom_sq && booking.standard_items?.length) {
                    const sortedItems = orderedItemShortNames
                        .map(name => booking.standard_items.find(item => item.item_name === name))
                        .filter(Boolean);

                    linenText = sortedItems
                        .map(item => `${item.standard_quantity} ${itemShortNames[item.item_name]}`)
                        .join(', ');
                } else {
                    const linenCounts = calculateLinen(
                        booking.single_beds,
                        booking.double_beds,
                        booking.bathrooms,
                        booking.is_beach_towel,
                        booking.super_king_beds
                    );
                    linenText = Object.values(linenCounts).filter(Boolean).join(', ');
                }

                if (linenText) {
                    lines.push(`<div>${linenText}</div>`);
                }
            }

            // 🔹 Mai De Luxe special prep text (like on screen)
            if (booking.client_name === "Mai De Luxe" && booking.standard_items?.length && !booking.is_linen_hire) {
                const sortedItems = orderedItemShortNames
                    .map(name => booking.standard_items.find(item => item.item_name === name))
                    .filter(Boolean);

                const mdlText = sortedItems
                    .map(item => `${item.standard_quantity} ${itemShortNames[item.item_name]}`)
                    .join(', ');

                lines.push(`<div><span style="background-color:silver;">Prepare From MDL:</span> ${mdlText}</div>`);
            }

            // 🔹 Laundry status line (mirror screen highlight)
            if (!booking.is_linen_hire) {
                if (booking.laundry_status) {
                    if (booking.laundry_status === 'completed') {
                        lines.push(
                            `<div>Laundry Status: ${booking.laundry_status}, Packed in ${booking.laundry_bags} bags</div>`
                        );
                    } else {
                        lines.push(
                            `<div>Laundry Status: <span style="color:red;">${booking.laundry_status}</span>, Packed in ${booking.laundry_bags} bags</div>`
                        );
                    }
                } else {
                    lines.push(
                        `<div><span style="color:red;">Laundry Status: Not Confirmed</span></div>`
                    );
                }
            }

            // 🔹 Linen add-ons & carpet
            const { linenSummary, hasCarpet } = getAddonSummary(booking.addons);
            if (linenSummary) {
                lines.push(`<div>${linenSummary}</div>`);
            }
            if (hasCarpet) {
                lines.push(
                    `<div>${linenSummary ? '' : 'Add-on: '}Prepare Spot Cleaner Machine</div>`
                );
            }

            // 🔹 Notes
            if (booking.warehouse_note || booking.booking_warehouse_notes) {
                const notesText = `${booking.warehouse_note || ''} ${booking.booking_warehouse_notes || ''}`.trim();
                if (notesText) {
                    lines.push(`<div><strong>Notes:</strong> ${notesText}</div>`);
                }
            }

            // Blank line between bookings
            lines.push('<div>&nbsp;</div>');
        });

        return lines.join('');
    };

    const handleCopyReport = async () => {
        if (!sortedReport.length) {
            return; // nothing to copy
        }

        try {
            const html = buildCopyHtmlForReport();
            const text = html
                .replace(/<br\s*\/?>/gi, '\n')
                .replace(/<\/div>/gi, '\n')
                .replace(/<[^>]+>/g, '')
                .replace(/\n{3,}/g, '\n\n')
                .trim();

            if (
                typeof navigator !== 'undefined' &&
                navigator.clipboard &&
                typeof window !== 'undefined' &&
                window.ClipboardItem
            ) {
                await navigator.clipboard.write([
                    new window.ClipboardItem({
                        'text/html': new Blob([html], { type: 'text/html' }),
                        'text/plain': new Blob([text], { type: 'text/plain' }),
                    }),
                ]);
            } else if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }

            setCopied(true);
        } catch (err) {
            console.error('Error copying report:', err);
        }
    };

    return (
        <div className="warehouse-container">
            <h2>Warehouse Duty Report</h2>
            <div className="wh-duty-date-selector">
                <label>Select Date:</label>
                <input
                    type="date"
                    value={selectedDate}
                    onChange={handleDateChange}
                    className="wh-duty-date-input"
                />
                <button
                    type="button"
                    onClick={() => fetchReport(selectedDate)}
                    className="wh-duty-fetch-button"
                >
                    Fetch Report
                </button>
                <button
                    type="button"
                    onClick={handleCopyReport}
                    className="wh-duty-copy-button"
                    disabled={sortedReport.length === 0}
                >
                    {copied ? 'Copied' : 'Copy Report'}
                </button>
            </div>

            {loading ? (
                <div>Loading report...</div>
            ) : error ? (
                <div className="error-message">{error}</div>
            ) : (
                <div className="report-output">

                    {/* 🔹 Edit Booking Popup */}
                    {editingBookingId && (
                        <EditBooking
                            bookingId={editingBookingId}
                            onClose={() => {
                                setEditingBookingId(null);
                                if (selectedDate) {
                                    fetchReport(selectedDate);
                                }
                            }}
                        />
                    )}

                    {/* ✅ ADMIN/MANAGER ONLY: LH Pickups Total (on screen only, not copied) */}
                    {(() => {
                        if (!isAdminOrManager) return null;
                        if (lhPickupLoading) return null;

                        const pickupEntries = buildLHPickupsEntries(lhPickupSummary, itemShortNames);
                        if (pickupEntries.length === 0) return null;

                        return (
                            <div className="wh-linen-hire-summary">
                                <strong style={{ color: '#1b7f3b' }}>
                                    LH Pickups Total:
                                </strong>{' '}
                                {pickupEntries.map(([label, count], idx) => (
                                    <span key={label}>
                                        <strong>{label}:</strong> {count} pcs
                                        {idx < pickupEntries.length - 1 ? ', ' : ''}
                                    </span>
                                ))}
                            </div>
                        );
                    })()}

                    {/* ✅ ADMIN/MANAGER ONLY: LH Actual Drop Off (from laundry counts) */}
                    {(() => {
                        if (!isAdminOrManager) return null;
                        if (lhDropoffLoading) return null;

                        const dropoffEntries = buildLHDropoffsEntries(lhDropoffSummary, itemShortNames);
                        if (dropoffEntries.length === 0) return null;

                        return (
                            <div className="wh-linen-hire-summary">
                                <strong style={{ color: '#0f4c81' }}>
                                    LH Actual Drop Off:
                                </strong>{' '}
                                {dropoffEntries.map(([label, count], idx) => (
                                    <span key={label}>
                                        <strong>{label}:</strong> {count} pcs
                                        {idx < dropoffEntries.length - 1 ? ', ' : ''}
                                    </span>
                                ))}
                            </div>
                        );
                    })()}

                    {/* ✅ FOR EVERYONE: Linen Hire Total (expected packing + add-ons) */}
                    {(() => {
                        const summary = getLinenHireSummary();
                        const order = ['BS', 'SMALL BS', 'SUPER KING BS', 'DC', 'SMALL DC', 'SUPER KING DC', 'PC', 'BT', 'HT', 'FT', 'BM', 'BEACH'];
                        const entries = order
                            .filter(key => summary[key] != null)
                            .map(key => [key, summary[key]]);

                        return entries.length > 0 ? (
                            <div className="wh-linen-hire-summary">
                                <strong style={{ color: '#FF5A60' }}>Linen Hire Total:</strong>{' '}
                                {entries.map(([label, count], idx) => (
                                    <span key={label}>
                                        <strong>{label}:</strong> {count} pcs
                                        {idx < entries.length - 1 ? ', ' : ''}
                                    </span>
                                ))}
                            </div>
                        ) : null;
                    })()}

                    {sortedReport.length > 0 ? (
                        sortedReport.map((booking, index) => {
                            const dutyInfo = getDutyInfo(booking);
                            const formattedPropertyId = `#${String(booking.property_id).padStart(3, '0')}`;
                            const areaColor = colorOf(booking.area);

                            const { linenSummary, hasCarpet } = getAddonSummary(booking.addons);

                            return (
                                <div key={index} className="warehouse-entry">
                                    {/* 🔹 Edit icon (admin/manager only) */}
                                    {isAdminOrManager && (
                                        <button
                                            type="button"
                                            className="warehouse-edit-button"
                                            onClick={() => setEditingBookingId(booking.id)}
                                            title="Edit booking"
                                        >
                                            <FontAwesomeIcon icon={faPencilAlt} />
                                        </button>
                                    )}

                                    <Link
                                        to={`/bookings/${booking.id}`}
                                        className="entry-title"
                                        style={{ color: areaColor }}
                                    >
                                        {index + 1}. {formattedPropertyId} - {booking.client_name} - {booking.building_name} - Unit {booking.unit_number} ({booking.bedrooms === 0 ? 'S' : booking.bedrooms} - {booking.bathrooms})
                                    </Link>

                                    <p>
                                        {dutyInfo.duty}
                                        {/* "+" only between two duties — a booking whose only
                                            job is amenities read "+ Pack Amenities". */}
                                        {dutyInfo.amenities && <span>{dutyInfo.duty ? ' + ' : ''}{dutyInfo.amenities}</span>}
                                    </p>

                                    {booking.first_amenities && (
                                        <p>
                                            <span style={{ backgroundColor: DISPENSER_HIGHLIGHT, fontWeight: 700 }}>
                                                PACK DISPENSERS
                                            </span>
                                            : {dispenserCounts(booking)}
                                        </p>
                                    )}

                                    {dutyInfo.linenText && <p>{dutyInfo.linenText}</p>}
                                    {dutyInfo.laundryText && <p>{dutyInfo.laundryText}</p>}
                                    {dutyInfo.laundryStatus && <p>{dutyInfo.laundryStatus}</p>}

                                    {/* 🔹 Linen add-ons summary */}
                                    {linenSummary && (
                                        <p className="wh-addons-line">
                                            {linenSummary}
                                        </p>
                                    )}

                                    {/* 🔹 Carpet / Upholstery deep clean */}
                                    {hasCarpet && (
                                        <p className="wh-addons-line">
                                            {linenSummary
                                                ? 'Prepare Spot Cleaner Machine'
                                                : 'Add-on: Prepare Spot Cleaner Machine'}
                                        </p>
                                    )}

                                    {(booking.warehouse_note || booking.booking_warehouse_notes) && (
                                        <p>Notes: {booking.warehouse_note} {booking.booking_warehouse_notes}</p>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <p>No report available for the selected date.</p>
                    )}
                </div>
            )}
        </div>
    );
}

export default Warehouse;